/**
 * /learn/programs — Learning Programs, an exact structural twin of
 * /learn/courses (product rule: one design; only the data differs):
 * header + count line, bucket tabs (In Progress / Upcoming / Completed),
 * grid of tiles, matching loading / error / empty states.
 */

import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { AcademicCapIcon } from '@heroicons/react/24/outline';
import { slideUp } from '@/lib/motion';
import PageHeader from '@/components/common/PageHeader';
import { getPrograms } from '../../api/me';
import ProgramBucketTabs from '../../components/learner/ProgramBucketTabs';
import type { ProgramWithProgress } from '../../components/learner/ProgramCard';
import { learnerKeys } from './DashboardPage';
import { usePlanFeatures } from '@/hooks/usePlanFeatures';

const ProgramsPage: React.FC = () => {
  // Learners never see plan talk (product rule 2026-07-24): on a plan without
  // programs there is nothing to show, so send them home quietly.
  const { programs: programsAllowed } = usePlanFeatures();
  const { data, isLoading, isError } = useQuery({
    queryKey: learnerKeys.programs,
    queryFn: getPrograms,
  });

  if (isLoading) {
    if (!programsAllowed) return <Navigate to="/learn/dashboard" replace />;

  return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-40 rounded bg-text-tertiary/10" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-56 rounded-2xl bg-text-tertiary/10" />
          ))}
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <p className="text-text-secondary">Failed to load programs. Please refresh.</p>
      </div>
    );
  }

  const programs = data as ProgramWithProgress[];
  const totalCount = programs.length;

  if (totalCount === 0) {
    return (
      <motion.div {...slideUp} className="space-y-6">
        <PageHeader title="Programs" root={{ label: 'Home', to: '/learn/dashboard' }} />
        <div className="card p-12 text-center">
          <div
            className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{
              background:
                'linear-gradient(135deg, rgb(var(--color-primary) / 0.18) 0%, rgb(var(--color-secondary) / 0.18) 100%)',
            }}
          >
            <AcademicCapIcon className="w-10 h-10 text-primary-500" />
          </div>
          <h3 className="text-base font-semibold text-text-primary mb-1">No Programs Yet</h3>
          <p className="text-sm text-text-secondary mb-4">
            Your team admin hasn't assigned any programs yet. Check back soon.
          </p>
          <Link
            to="/learn/dashboard"
            className="inline-flex items-center gap-1.5 text-sm font-bold text-primary-500 hover:underline"
          >
            ← Back to Dashboard
          </Link>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div {...slideUp} className="space-y-6">
      <header>
        <PageHeader title="Programs" root={{ label: 'Home', to: '/learn/dashboard' }} />
        <p className="text-sm text-text-secondary mt-2">
          {totalCount} {totalCount === 1 ? 'program' : 'programs'} assigned to you.
        </p>
      </header>
      <ProgramBucketTabs programs={programs} />
    </motion.div>
  );
};

export default ProgramsPage;
